import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// The server (kin-chat Edge Function) is the single source of truth for daily
// message limits. This hook only tracks a UI flag so we can disable the input
// after the server has told us (via a 429) that the limit is hit. We persist
// that flag in localStorage so the disabled UI survives tab navigation /
// reloads, and clears when the user's day rolls over.
//
// ⚠️ THE DAY BOUNDARY IS NOT COMPUTED HERE. It comes from the user_cap_day()
// SQL helper — the same oracle kin-chat's cap check and
// increment_message_count() call. Do NOT go back to deriving "today" on the
// device (neither toISOString() nor formatLocalDate()):
//   - UTC clears this flag at 8pm ET while the server still blocks.
//   - Device-local clears it at the user's own midnight while the server uses
//     profiles.timezone with an America/New_York fallback — so a Pacific user
//     whose timezone hasn't been captured yet sees "reset" from 9pm to midnight
//     Pacific while every send still 429s.
// Both mismatches present AT THE PAYWALL, the worst possible place to be wrong.
// One clock, asked over the wire. See migration
// 20260725120000_message_cap_per_user_timezone.sql.

const STORAGE_KEY = "kin_limit_reached";
/** Pre-#350 flags were date-suffixed keys (`kin_limit_reached_2026-07-25`). */
const LEGACY_KEY_PREFIX = "kin_limit_reached_";

export type LimitRecord = {
  userId: string;
  /** The user_cap_day() value (YYYY-MM-DD) at the moment the cap was hit. */
  capDay: string;
  reached: true;
};

// ─── Storage ────────────────────────────────────────────────────────────────

const readRecord = (): LimitRecord | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LimitRecord> | null;
    if (!parsed?.userId || !parsed?.capDay || parsed.reached !== true) return null;
    return { userId: parsed.userId, capDay: parsed.capDay, reached: true };
  } catch {
    return null;
  }
};

const writeRecord = (record: LimitRecord) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore (private mode, quota, etc.)
  }
};

const clearRecord = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** Drop the old date-keyed flags so they don't sit in localStorage forever. */
const purgeLegacyKeys = () => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LEGACY_KEY_PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
};

// ─── External clears (the upgrade path) ─────────────────────────────────────
//
// A free->pro upgrade has to drop this flag, but the upgrade is not detected
// from a React call site: fireEntitlementFlipped() (src/lib/entitlementFlip.ts)
// is a plain module function and cannot reach the hook's clearLimitFlag(). It
// calls clearPersistedLimitFlag() instead, which drops the persisted record AND
// pushes every mounted hook instance back to "not limited" via this listener
// set.
//
// This is the DURABLE half of #379. Index.tsx's Pro exemption unblocks the
// composer on the render the override flips, but without this the
// kin_limit_reached record survives a force-quit and blocks a paying user again
// on her next cold open — still holding "Unlimited messages" hostage until local
// midnight.
const clearListeners = new Set<() => void>();

/** Drop the persisted cap flag from outside React (free->pro upgrade). */
export const clearPersistedLimitFlag = () => {
  clearRecord();
  purgeLegacyKeys();
  clearListeners.forEach((listener) => listener());
};

// ─── The day oracle (session-cached user_cap_day RPC) ───────────────────────

let dayCache: { userId: string; capDay: string } | null = null;
let inFlight: { userId: string; promise: Promise<string | null> } | null = null;

const fetchCapDay = async (userId: string): Promise<string | null> => {
  try {
    // p_user_id is passed explicitly: user_cap_day() takes the id rather than
    // reading auth.uid(), which is how kin-chat has to call it (service role
    // has no auth.uid()). Same argument, same function, same answer.
    const { data, error } = await supabase.rpc(
      "user_cap_day" as never,
      { p_user_id: userId } as never,
    );
    if (error || !data) {
      if (error) console.warn("user_cap_day lookup failed:", error.message);
      return null;
    }
    return String(data).slice(0, 10);
  } catch (e) {
    console.warn("user_cap_day lookup failed:", e);
    return null;
  }
};

/** Resolve the user's cap day. Cached for the session; `force` re-asks the
 *  server (used on foreground, so a phone that sat idle across midnight can't
 *  keep answering with yesterday). A failed forced refresh falls back to the
 *  cached value rather than pretending we know nothing. */
const getCapDay = (userId: string, force = false): Promise<string | null> => {
  if (!force && dayCache?.userId === userId) return Promise.resolve(dayCache.capDay);
  if (!force && inFlight?.userId === userId) return inFlight.promise;

  const promise = fetchCapDay(userId)
    .then((capDay) => {
      if (capDay) {
        dayCache = { userId, capDay };
        return capDay;
      }
      return dayCache?.userId === userId ? dayCache.capDay : null;
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });

  inFlight = { userId, promise };
  return promise;
};

// ─── Pure decision (unit-tested in src/hooks/__tests__) ─────────────────────

/** Given the stored flag and what the server says today is, decide what the UI
 *  should show and whether the stored record is now garbage.
 *
 *  When the server day can't be resolved we KEEP blocking: the server almost
 *  certainly still is, and a false "you're reset" at the paywall is the exact
 *  bug this change exists to kill. So the flag can only ever be wrong in the
 *  conservative direction, and every foreground re-asks. */
export const decideLimitFlag = (
  record: LimitRecord | null,
  userId: string | undefined,
  serverCapDay: string | null,
): { limitReached: boolean; dropRecord: boolean } => {
  if (!record) return { limitReached: false, dropRecord: false };
  // Different account on this device (or signed out): not our flag.
  if (!userId || record.userId !== userId) return { limitReached: false, dropRecord: true };
  if (!serverCapDay) return { limitReached: true, dropRecord: false };
  if (record.capDay !== serverCapDay) return { limitReached: false, dropRecord: true };
  return { limitReached: true, dropRecord: false };
};

// ─── Hook ───────────────────────────────────────────────────────────────────

export const useMessageLimit = () => {
  const { user } = useAuth();
  const userId = user?.id;
  // Start from the last persisted state (pessimistic: if we were blocked, stay
  // blocked until the server says the day rolled over).
  const [limitReached, setLimitReached] = useState<boolean>(() => readRecord() !== null);
  const purgedRef = useRef(false);
  // Bumped on every clear. Both async paths below (reconcile, markLimitReached)
  // read the day oracle over the wire, so a clear can land mid-flight; without
  // this guard an in-flight reconcile that started while the flag was still set
  // would resolve afterwards and set limitReached back to true — resurrecting a
  // block we just deliberately released on upgrade.
  const clearGenRef = useRef(0);

  /** Reconcile the stored flag against user_cap_day(). */
  const reconcile = useCallback(
    async (force: boolean) => {
      const record = readRecord();
      if (!record) {
        setLimitReached(false);
        return;
      }
      // Auth hasn't resolved yet — never clear a flag on a missing user id.
      if (!userId) return;

      const gen = clearGenRef.current;
      const capDay = await getCapDay(userId, force);
      if (clearGenRef.current !== gen) return;
      const { limitReached: next, dropRecord } = decideLimitFlag(record, userId, capDay);
      if (dropRecord) clearRecord();
      setLimitReached(next);
    },
    [userId],
  );

  // A clear can originate outside React (fireEntitlementFlipped on upgrade).
  useEffect(() => {
    const onExternalClear = () => {
      clearGenRef.current += 1;
      setLimitReached(false);
    };
    clearListeners.add(onExternalClear);
    return () => {
      clearListeners.delete(onExternalClear);
    };
  }, []);

  useEffect(() => {
    if (!purgedRef.current) {
      purgedRef.current = true;
      purgeLegacyKeys();
    }
    void reconcile(false);
  }, [reconcile]);

  // Invalidate on foreground: a mom who backgrounds the app at 11:50pm and
  // comes back at 12:10am must not be looking at yesterday's day key.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") void reconcile(true);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [reconcile]);

  const markLimitReached = useCallback(() => {
    setLimitReached(true);
    if (!userId) return;
    // Stamp the record with the server's day. force=true because this fires the
    // instant a 429 came back — the network is up, and an app left open across
    // midnight must not stamp yesterday. If we can't date the flag we keep it in
    // memory only: better to re-earn the 429 next launch than to persist an
    // undated block that might hold over into a new day.
    void (async () => {
      const gen = clearGenRef.current;
      const capDay = await getCapDay(userId, true);
      if (!capDay) return;
      // Cleared while we were asking (e.g. the upgrade landed) — do not re-stamp.
      if (clearGenRef.current !== gen) return;
      writeRecord({ userId, capDay, reached: true });
    })();
  }, [userId]);

  const clearLimitFlag = useCallback(() => {
    // Also notifies this instance's own listener (bumps clearGenRef); the
    // explicit setLimitReached keeps behavior identical on the off chance this
    // fires before the subscribe effect has run.
    clearPersistedLimitFlag();
    setLimitReached(false);
  }, []);

  return { limitReached, markLimitReached, clearLimitFlag };
};
